import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** Backup module (Datto SaaS Protection) — types mirror server/backup/. */

export type BackupHealth = 'healthy' | 'warning' | 'failing' | 'unknown';

export interface SaasMapping {
  source: 'cipp' | 'manual' | 'ignored' | 'none';
  tenant: { customerId: string; displayName: string; defaultDomainName: string } | null;
  companyId: number | null;
  companyName: string | null;
  mappedBy: string | null;
}

export interface SeatCounts {
  total: number;
  billable: number;
  byState: Record<string, number>;
  byType: Record<string, number>;
  archivedBillable: number;
}

export interface SaasCustomerSummary {
  saasCustomerId: number;
  name: string;
  /** The tenant's *.onmicrosoft.com prefix. */
  domain: string;
  productType: string;
  retentionType: string;
  seatsUsed: number;
  mapping: SaasMapping;
  backupPct: number | null;
  health: BackupHealth;
  apps: { app: string; health: BackupHealth; lastFullyProtected: string | null }[];
  /** null until the background worker has loaded this customer. */
  seats: SeatCounts | null;
  usedBytes: number | null;
  coverage: { members: number; protectedMembers: number; pct: number | null; notProtected: number; leavers: number } | null;
  fetchedAt: string | null;
  error: string | null;
}

export interface BackupTrendPoint { day: string; backupPct: number | null; seats: number; billable: number; archivedBillable: number; notProtected: number }

export interface SaasCustomersResponse {
  items: SaasCustomerSummary[];
  fetchedAt: string;
  refresh: { running: boolean; done: number; total: number; startedAt: string | null; finishedAt: string | null; loaded: number };
  trend: BackupTrendPoint[];
}

export interface AppDay { daysAgo: number; status: string; health: BackupHealth; active: number; withBackup: number; perfect: number }
export interface AppHealth { app: string; health: BackupHealth; lastFullyProtected: string | null; usedBytes: number | null; latest: AppDay | null; days: AppDay[] }
export interface Seat { remoteId: string; mainId: string; name: string; type: string; state: string; billable: boolean; dateAdded: string | null }

export interface SaasCoverage {
  tenant: { customerId: string; displayName: string; defaultDomainName: string };
  members: number;
  protectedMembers: number;
  pct: number | null;
  notProtected: { id: string; upn: string; displayName: string; seatState: string | null; isAdmin: boolean }[];
  leavers: (Seat & { reason: string })[];
  asOf: string | null;
  error: string | null;
}

export interface SaasCustomerDetail {
  customer: SaasCustomerSummary;
  apps: AppHealth[];
  seats: Seat[];
  coverage: SaasCoverage | null;
  loaded: boolean;
  trend: BackupTrendPoint[];
}

/** Background-cached on the server; poll while the first refresh is still loading customers. */
/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useSaasCustomers(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<SaasCustomersResponse>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.mapping.companyId)) }));
  return useQuery({
    select,
    queryKey: ['backup', 'saas', 'customers'],
    queryFn: () => api.get<SaasCustomersResponse>('/api/backup/saas/customers'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const r = q.state.data?.refresh;
      return r && (r.running || r.loaded < (q.state.data?.items.length ?? 0)) ? 10_000 : false;
    },
  });
}

export function useSaasCustomer(id: number | undefined) {
  return useQuery({
    queryKey: ['backup', 'saas', 'customer', id],
    queryFn: () => api.get<SaasCustomerDetail>(`/api/backup/saas/customers/${id}`),
    enabled: !!id,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (q.state.data && !q.state.data.loaded ? 10_000 : false),
  });
}

export function useRefreshSaasCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.get<SaasCustomerDetail>(`/api/backup/saas/customers/${id}?fresh=1`),
    onSuccess: (d, id) => {
      qc.setQueryData(['backup', 'saas', 'customer', id], d);
      qc.invalidateQueries({ queryKey: ['backup', 'saas', 'customers'] });
    },
  });
}

export function useSetSaasMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: number; value: { companyId: number; companyName: string } | { ignored: true } | null }) =>
      value ? api.put(`/api/backup/saas/map/${id}`, value) : api.del(`/api/backup/saas/map/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup', 'saas'] }),
  });
}

export interface UserBackup {
  state: 'found' | 'no-seat' | 'no-customer' | 'loading';
  saasCustomerId?: number;
  seat?: Seat | null;
  exchange?: { health: BackupHealth; lastFullyProtected: string | null } | null;
  oneDrive?: { health: BackupHealth; lastFullyProtected: string | null } | null;
}

/** One M365 user's SaaS Protection seat — for the ticket's Microsoft 365 card. Cache-only on the server. */
export function useUserBackup(tenantId: string | undefined, objectId: string | undefined) {
  return useQuery({
    queryKey: ['backup', 'saas', 'user', tenantId, objectId],
    queryFn: () => api.get<UserBackup>(`/api/backup/saas/users/${tenantId}/${objectId}`),
    enabled: !!tenantId && !!objectId,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
